/**
 * PlatformConfigureAddressDrawer
 * ==============================
 * Platform-side replacement for `components/common/ConfigureAddressDrawer`.
 *
 * Same prop API, same business logic (`useAddressData`, validation flow,
 * `excludeFieldsOnSuccess` stripping, toast on create-success,
 * default-location subheader swap). The migration is purely structural:
 *
 *   <Drawer> + <DrawerTitle> + <DrawerFooter>  ->  DS <Drawer>
 *   <Button variant="outlined|contained">      ->  DS-themed variants
 *   manual body padding wrapper                ->  the DS drawer body handles it
 *   isSavingProfile gating one button          ->  DS `loading` covers the paper
 *
 * Lives in `platform/migrate/` until admin migrates and this becomes
 * canonical. Consumers swap import path + nothing else, with one exception:
 * `customGapSize` is not accepted here, because this drawer fixes its own
 * field gap rather than letting callers set it.
 */
import React from "react";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Drawer } from "@outpave/ds";
import ConfigureAddress from "components/common/ConfigureAddress";
import { useAddressData } from "hooks/useAddressData";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import {
  ConfigureAddressValidationType,
  FormikAddress,
} from "utils/addressFormat";

interface PlatformConfigureAddressDrawerProps {
  open: boolean;
  drawerTitle: string[];

  /** Use when address data is part of a bigger whole (e.g. user). */
  initialValues?: FormikAddress;
  /** Use when fetching existing address data from the API. */
  selectedAddressId?: string;

  header?: string;
  subheader?: string;
  readOnly?: boolean;
  customAddressName?: boolean;
  validationType?: ConfigureAddressValidationType;
  excludeFieldsOnSuccess?: boolean;
  onClose: () => void;
  onSuccess: (values: FormikAddress) => void;
  setReadOnly?: (readOnly: boolean) => void;
}

const PlatformConfigureAddressDrawer: React.FC<
  PlatformConfigureAddressDrawerProps
> = ({
  open,
  drawerTitle,
  header,
  subheader,
  readOnly = false,
  customAddressName = false,
  initialValues,
  selectedAddressId,
  validationType = ConfigureAddressValidationType.VALIDATE,
  excludeFieldsOnSuccess = true,
  onClose,
  onSuccess,
  setReadOnly,
}) => {
  const { t } = useTranslation();

  const {
    formikAddress,
    specificLoadingStates,
    handleSaveAddress,
    resetFormToInitialValues,
  } = useAddressData({
    addressId: selectedAddressId,
    initialValues: initialValues,
    type: validationType,
  });

  const { validateAndTouch } = useFormikHelpers(formikAddress);

  const handleCloseDrawer = () => {
    if (isSavingProfile) return;
    resetFormToInitialValues();
    onClose();
  };

  const handleSave = async () => {
    if (validationType === ConfigureAddressValidationType.CREATE) {
      const success = await handleSaveAddress();
      // Bail on failed save so we don't fire onSuccess (and don't close
      // the drawer mid-flow). The legacy component had a fall-through
      // that called onSuccess twice on success and once on failure.
      if (!success) return;
      toast.success(
        selectedAddressId ? t("Location updated") : t("Location added"),
      );
      // CREATE mode passes full values (consumers want geocode +
      // defaultLocation on first save).
      onSuccess(formikAddress.values);
      return;
    }
    // VALIDATE mode strips internal-only fields before handing the
    // values back to the consumer (so they don't echo into nested
    // formik shapes that don't expect them).
    let valuesToSend = formikAddress.values;
    if (excludeFieldsOnSuccess) {
      const { geocode, defaultLocation, ...rest } = formikAddress.values;
      valuesToSend = rest;
    }
    onSuccess(valuesToSend);
  };

  const isSavingProfile =
    specificLoadingStates.creatingAddress ||
    specificLoadingStates.updatingAddress;

  const isDefaultLocation = formikAddress.values?.defaultLocation;
  const subheaderToShow = isDefaultLocation
    ? t("DefaultCompanyLocationSubtitle")
    : subheader;

  return (
    <Drawer
      open={open}
      onClose={handleCloseDrawer}
      title={drawerTitle}
      loading={isSavingProfile}
      footer={
        <>
          <Button variant="default-outlined" onClick={handleCloseDrawer}>
            {t("Back")}
          </Button>
          {!readOnly && (
            <Button
              disabled={isSavingProfile || !formikAddress.dirty}
              onClick={async () => {
                const isValid = await validateAndTouch();
                if (isValid) {
                  handleSave();
                }
              }}
            >
              {t("Save")}
            </Button>
          )}
          {readOnly && setReadOnly && !isDefaultLocation && (
            <Button
              variant="default-outlined"
              onClick={() => setReadOnly(false)}
            >
              {t("Edit")}
            </Button>
          )}
        </>
      }
    >
      <ConfigureAddress
        subtitle={subheaderToShow}
        formik={formikAddress}
        customAddressName={customAddressName}
        readOnly={readOnly}
        customGapSize="var(--space-5)"
      />
    </Drawer>
  );
};

export default PlatformConfigureAddressDrawer;
